import React, { useState, useEffect, useCallback } from 'react';
import './ComparisonSlideout.css';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';
import { ShareIcon, EyeIcon } from '../Button/icons';
import { chatService } from '../../services/chatService';
import type { ComparisonResponse } from '../../types/chat';

export interface ComparisonProduct {
  /** Product ID */
  id: string;
  /** Product name */
  productName: string;
  /** Product description */
  description: string;
  /** URL for the product image */
  imageUrl?: string;
  /** Alt text for the product image */
  imageAlt?: string;
  /** Match percentage (0-100) */
  matchPercentage?: number;
  /** Array of feature tags/badges */
  tags?: string[];
  /** Key features list */
  features?: string[];
  /** Whether this is marked as top recommendation */
  isTopRecommendation?: boolean;
  /** Backend model name for API calls */
  model?: string;
}

export interface ComparisonSlideoutProps {
  /** Whether the slideout is open */
  isOpen: boolean;
  /** Handler for closing the slideout */
  onClose: () => void;
  /** Array of products to compare */
  products?: ComparisonProduct[];
  /** Handler for removing a product from comparison */
  onRemoveProduct?: (productId: string) => void;
  /** Handler for viewing product details */
  onViewDetails?: (productId: string) => void;
  /** Handler for sharing comparison */
  onShare?: () => void;
  /** Optional CSS class name */
  className?: string;
}

export const ComparisonSlideout: React.FC<ComparisonSlideoutProps> = ({
  isOpen,
  onClose,
  products = [],
  onRemoveProduct,
  onViewDetails,
  onShare,
  className = '',
}) => {
  const [comparisonData, setComparisonData] = useState<ComparisonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract model names from products using best practices
  const extractModelNames = (products: ComparisonProduct[]): string[] => {
    return products
      .map(product => {
        // Try multiple strategies to extract model name
        if (product.model) {
          return product.model;
        }
        
        // Extract from product name - look for patterns like UC-M70-UA, CCS-UC-1, etc.
        const modelMatch = product.productName.match(/([A-Z]{2,}-[A-Z0-9-]+)/i);
        if (modelMatch) {
          return modelMatch[1];
        }
        
        // Fallback: use the full product name if no pattern found
        return product.productName;
      })
      .filter(Boolean); // Remove any null/undefined values
  };

  // Fetch comparison data from API
  const fetchComparisonData = useCallback(async (products: ComparisonProduct[]) => {
    if (products.length === 0) {
      setComparisonData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const modelNames = extractModelNames(products);
      console.log('Fetching comparison for models:', modelNames);
      
      const response = await chatService.compareProducts(modelNames);
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      if (!response.models || !Array.isArray(response.models)) {
        throw new Error('Invalid comparison data: missing or invalid models array');
      }
      
      if (response.models.length === 0) {
        throw new Error('No comparison data available for the selected products');
      }
      
      // Validate each model has required fields
      const invalidModels = response.models.filter(model => 
        !model.name || !model.features || typeof model.features !== 'object'
      );
      
      if (invalidModels.length > 0) {
        console.warn('Some models have invalid structure:', invalidModels);
        // Filter out invalid models but continue if we have at least one valid model
        response.models = response.models.filter(model => 
          model.name && model.features && typeof model.features === 'object'
        );
        
        if (response.models.length === 0) {
          throw new Error('All comparison data is invalid or incomplete');
        }
      }
      
      console.log('✅ Comparison data validated successfully');
      setComparisonData(response);
    } catch (err) {
      console.error('Failed to fetch comparison data:', err);
      
      let errorMessage = 'Failed to load comparison data';
      
      if (err instanceof Error) {
        // Handle specific error types
        if (err.message.includes('fetch')) {
          errorMessage = 'Network error: Unable to connect to server';
        } else if (err.message.includes('JSON')) {
          errorMessage = 'Server returned invalid data format';
        } else if (err.message.includes('Invalid')) {
          errorMessage = err.message;
        } else {
          errorMessage = `Error: ${err.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch comparison data when products change
  useEffect(() => {
    if (isOpen && products.length > 0) {
      fetchComparisonData(products);
    } else {
      setComparisonData(null);
      setError(null);
    }
  }, [isOpen, products, fetchComparisonData]);

  // Handle overlay click to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when slideout is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleRemoveProduct = (productId: string) => {
    onRemoveProduct?.(productId);
  };

  const handleViewDetails = (productId: string) => {
    onViewDetails?.(productId);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="comparison-slideout__overlay"
      onClick={handleOverlayClick}
    >
      <div className={`comparison-slideout ${className}`}>
        {/* Header */}
        <div className="comparison-slideout__header">
          <div className="comparison-slideout__title-section">
            <h1 className="comparison-slideout__title">
              PRODUCT COMPARISON
            </h1>
            <p className="comparison-slideout__subtitle">
              {products.length} product{products.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <div className="comparison-slideout__header-actions">
            <Button
              icon={<ShareIcon />}
              type="outline"
              size="medium"
              onClick={onShare}
            >
              SHARE COMPARISON
            </Button>
            <button 
              className="comparison-slideout__close-button"
              onClick={onClose}
              aria-label="Close comparison"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>


        {/* Main content area */}
        <div className="comparison-slideout__content">
          {products.length === 0 ? (
            // Empty state
            <div className="comparison-slideout__empty-state">
              <div className="comparison-slideout__empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </div>
              <h3>No products selected for comparison</h3>
              <p>Select products using the "Compare" checkbox to start comparing.</p>
            </div>
          ) : error ? (
            // Error state
            <div className="comparison-slideout__error-state">
              <div className="comparison-slideout__error-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h3>Comparison Temporarily Unavailable</h3>
              <p>{error}</p>
              <div className="comparison-slideout__error-help">
                <p><strong>What you can try:</strong></p>
                <ul>
                  <li>Click "Try Again" to retry the comparison</li>
                  <li>Try comparing different products</li>
                  <li>Check your internet connection</li>
                  <li>If the problem persists, please contact support</li>
                </ul>
              </div>
              <div className="comparison-slideout__error-actions">
                <Button
                  type="primary"
                  size="medium"
                  onClick={() => fetchComparisonData(products)}
                >
                  Try Again
                </Button>
                <Button
                  type="outline"
                  size="medium"
                  onClick={onClose}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : isLoading ? (
            // Loading state
            <div className="comparison-slideout__loading-state">
              <LoadingSpinner size={48} />
              <h3>Loading comparison...</h3>
              <p>Analyzing product features and specifications</p>
            </div>
          ) : (
            <div className="comparison-slideout__side-by-side">
              {comparisonData && comparisonData.models ? (
                <>
                  {comparisonData.key_difference && (
                    <div className="comparison-slideout__key-difference-header">
                      <h3>Key Differences Analysis</h3>
                      <div className="comparison-slideout__key-difference">
                        <p>{comparisonData.key_difference}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="comparison-slideout__comparison-table">
                    {/* Product headers */}
                    <div className="comparison-slideout__table-header">
                      <div className="comparison-slideout__feature-column">
                        <span>Features</span>
                      </div>
                      {comparisonData.models.map((model, index) => (
                        <div key={model.name} className="comparison-slideout__product-column">
                          <div className="comparison-slideout__product-header">
                            <div className="comparison-slideout__product-title">
                              <h3>{model.name}</h3>
                              {products[index]?.isTopRecommendation && (
                                <span className="comparison-slideout__top-badge">
                                  Top Rec
                                </span>
                              )}
                            </div>
                            <button
                              className="comparison-slideout__remove-product"
                              onClick={() => handleRemoveProduct(products[index]?.id)}
                              aria-label={`Remove ${model.name} from comparison`}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </div>
                          {model.image && (
                            <div className="comparison-slideout__product-image">
                              <img 
                                src={model.image} 
                                alt={model.name}
                                className="comparison-slideout__product-img"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Feature comparison rows */}
                    <div className="comparison-slideout__table-body">
                      {Object.keys(comparisonData.models[0]?.features || {}).map((featureName) => (
                        <div key={featureName} className="comparison-slideout__feature-row">
                          <div className="comparison-slideout__feature-name">
                            {featureName}
                          </div>
                          {comparisonData.models.map((model) => (
                            <div key={`${model.name}-${featureName}`} className="comparison-slideout__feature-value">
                              {model.features[featureName] || 'N/A'}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* Product actions */}
                    <div className="comparison-slideout__table-footer">
                      <div className="comparison-slideout__feature-column">
                        <span>Actions</span>
                      </div>
                      {comparisonData.models.map((model, index) => (
                        <div key={`actions-${model.name}`} className="comparison-slideout__product-actions">
                          <Button
                            type="outline"
                            size="small"
                            icon={<EyeIcon />}
                            iconPosition="left"
                            onClick={() => handleViewDetails(products[index]?.id)}
                          >
                            VIEW DETAILS
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="comparison-slideout__products-grid">
                  {products.map((product) => (
                    <div key={product.id} className="comparison-slideout__product-card">
                      <div className="comparison-slideout__product-header">
                        <div className="comparison-slideout__product-title">
                          <h3>{product.productName}</h3>
                          {product.isTopRecommendation && (
                            <span className="comparison-slideout__top-badge">
                              Top Rec
                            </span>
                          )}
                        </div>
                        <button
                          className="comparison-slideout__remove-product"
                          onClick={() => handleRemoveProduct(product.id)}
                          aria-label={`Remove ${product.productName} from comparison`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                      {product.imageUrl && (
                        <div className="comparison-slideout__product-image">
                          <img 
                            src={product.imageUrl} 
                            alt={product.imageAlt || product.productName}
                            className="comparison-slideout__product-img"
                          />
                        </div>
                      )}
                      <div className="comparison-slideout__product-details">
                        <p className="comparison-slideout__product-description">
                          {product.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with comparison tools - placeholder */}
        <div className="comparison-slideout__footer">
          <div className="comparison-slideout__footer-content">
            <span className="comparison-slideout__footer-text">
              Need help deciding? Our AI can analyze these products for you.
            </span>
            <Button
              type="primary"
              size="medium"
              disabled
            >
              ASK AI TO COMPARE
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
